<template>
    <div class="space-y-3">
        <DsSingleSelect
            v-model="selectedRoleOption"
            v-bind="clientAttrs?.role"
            :id="`role-${client.id}`"
            :error="clientErrors[ClientValidationErrorKeys.ROLE]"
            label="Role"
            :options="roleOptions"
            placeholder="Select a role"
            required
            :showSearchInput="false"
            @optionSelected="$emit('fieldInput', ClientValidationErrorKeys.ROLE)"
        />
        <DsTextInput
            v-model="client.first"
            v-bind="clientAttrs?.first"
            :id="`firstName-${client.id}`"
            :error="clientErrors[ClientValidationErrorKeys.FIRST_NAME]"
            label="First name"
            :maxlength="512"
            type="text"
            @input="$emit('fieldInput', ClientValidationErrorKeys.FIRST_NAME)"
        />
        <DsTextInput
            v-model="client.last"
            v-bind="clientAttrs?.last"
            :id="`lastName-${client.id}`"
            :error="clientErrors[ClientValidationErrorKeys.LAST_NAME]"
            label="Last name"
            :maxlength="512"
            type="text"
            @input="$emit('fieldInput', ClientValidationErrorKeys.LAST_NAME)"
        />
        <DsTextInput
            v-model="client.email"
            v-bind="clientAttrs?.email"
            :id="`email-${client.id}`"
            :disabled="isClientEmailDisabled"
            :error="clientErrors[ClientValidationErrorKeys.EMAIL]"
            label="Email"
            :maxlength="100"
            required
            type="text"
            @input="$emit('fieldInput', ClientValidationErrorKeys.EMAIL)"
        />
        <DsPhoneInput
            v-model="client.phone"
            v-bind="clientAttrs?.phone"
            :id="`phone-${client.id}`"
            :error="clientErrors[ClientValidationErrorKeys.PHONE]"
            label="Phone number"
            :required="isPhoneRequired"
            @countryChanged="$emit('fieldInput', ClientValidationErrorKeys.PHONE)"
            @input="$emit('fieldInput', ClientValidationErrorKeys.PHONE)"
        />
    </div>
</template>

<script setup lang="ts">
import DsPhoneInput from '@/components/design-system/form/input/DsPhoneInput/DsPhoneInput.vue';
import type {
    ClientAttrs,
    ClientFormData,
    TypedClientError,
} from '@/components/Views/Company/File/Client/client-info-form.types';
import { ClientValidationErrorKeys } from '@/components/Views/Company/File/Client/client-info-form.types';
import type { DsSingleSelectRoleOption } from '@/components/Views/Company/File/file.types';
import { ClientTypeEnum } from '@/components/Views/Company/File/file.types';
import { toSentenceCase } from '@/shared/utils/strings';
import { useUserStore } from '@/stores/company/user.store';
import type { Role } from '@/types/closinglock.types';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import DsSingleSelect from '@ds/singleselect/DsSingleSelect.vue';
import { computed } from 'vue';

interface ClientFormFieldsProps {
    clientAttrs?: ClientAttrs;
    clientErrors: TypedClientError;
    isClientEmailDisabled?: boolean;
}

withDefaults(defineProps<ClientFormFieldsProps>(), {
    isClientEmailDisabled: false,
});

defineEmits<{
    (e: 'fieldInput', field: ClientValidationErrorKeys): void;
}>();

const client = defineModel<ClientFormData>('client', { required: true });

const userStore = useUserStore();

const roleOptions: DsSingleSelectRoleOption[] = Object.values(ClientTypeEnum).map(role => ({
    id: role,
    label: toSentenceCase(role),
    value: role,
}));

const selectedRoleOption = computed<DsSingleSelectRoleOption | undefined>({
    get() {
        return roleOptions.find(option => option.value === client.value.role);
    },
    set(option) {
        client.value.role = option?.value;
    },
});

const isPhoneRequired = computed(
    () => (client.value.role ? userStore.requireMfaClientTypes.includes(client.value.role as Role) : false),
    // assertion is needed because we have duplicate type declarations for client role -- @todo cleanup in [CLENG-883]
);
</script>
